
import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import './SettingPageMain.css';

import Profile from './Profile';
import PermissionsPage from './PermissionsPage';
import Department from './Department';
import Designation from './Designation';
import ReportingManager from './ReportingManager';
import Organization from '../pages/Organization';
import PlaceholderComponent from '../pages/PlaceholderComponent';
import MaintenanceSettingsPage from './MaintenanceSettingsPage';

const LOCAL_STORAGE_USER_KEY = 'zTaskUserDetails';

const SettingPageMain = ({ onProfileUpdate }) => {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isReportingManager, setIsReportingManager] = useState(false);
  const [isFleetManager, setIsFleetManager] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const location = useLocation();

  const checkUserRole = useCallback(() => {
    setIsLoadingRole(true);
    try {
      const userDataString = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        const userRole = userData?.role?.name?.toUpperCase().replace(/\s+/g, '_');
        
        setIsSuperAdmin(userRole === 'SUPER_ADMIN' || userRole === 'ADMIN');
        setIsReportingManager(userRole === 'REPORTING_MANAGER' || userRole === 'REPORTING MANAGER');
        setIsFleetManager(userRole === 'FLEET_MANAGER');
      } else {
        setIsSuperAdmin(false);
        setIsReportingManager(false);
        setIsFleetManager(false);
      }
    } catch (error) {
      console.error("SettingPageMain: Error checking role:", error);
      setIsSuperAdmin(false);
      setIsReportingManager(false);
      setIsFleetManager(false);
    }
    setIsLoadingRole(false);
  }, []);

  useEffect(() => {
    checkUserRole();
    const handleStorageChange = (event) => {
      if (event.key === LOCAL_STORAGE_USER_KEY || event.key === null) {
        checkUserRole();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkUserRole, location.pathname]);

  const navItems = [
    { path: "/settings/profile", label: "Profile", icon: "👤", adminOnly: false },
    { path: "/settings/departments", label: "Departments", icon: "🏢", adminOnly: true },
    { path: "/settings/designation", label: "Designation", icon: "🏷️", adminOnly: true },
    { path: "/settings/reporting-manager", label: "Reporting Manager", icon: "📈", adminOnly: true },
    { path: "/settings/organization", label: "Organization", icon: "📊", adminOnly: true },
    { path: "/settings/permissions", label: "Permissions", icon: "🔑", adminOnly: true },
    { path: "/settings/maintenance", label: "Maintenance", icon: "🛠️", adminOnly: true, fleetOk: true },
  ];

  const accessibleNavItems = navItems.filter(item => {
    if (!item.adminOnly) {
      return true;
    }
    if (isSuperAdmin) {
      return true;
    }
    if (isReportingManager && item.path === "/settings/permissions") {
      return true;
    }
    if (isFleetManager && item.fleetOk) {
      return true;
    }
    return false;
  });

  if (isLoadingRole) {
    return (
      <div className="settings-page-container">
        <h1 className="settings-title">Settings</h1>
        <div className="settings-loading-placeholder">Loading...</div>
      </div>
    );
  }

  return (
    <div className="settings-page-container">
      <h1 className="settings-title">Settings</h1>
      <nav className="settings-top-navbar">
        {accessibleNavItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => `top-nav-item ${isActive ? 'active' : ''}`}
          >
            <span role="img" aria-label={item.label.toLowerCase()}>{item.icon}</span> {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="settings-content-area">
        <Routes>
          <Route path="/" element={<Navigate to="/settings/profile" replace />} />
          <Route path="profile" element={<Profile />} />
          <Route path="notifications" element={<PlaceholderComponent pageName="Notifications Settings" />} />
          
          {/* Routes only accessible to SUPER_ADMIN */}
          <Route path="departments" element={isSuperAdmin ? <Department /> : <Navigate to="/settings/profile" replace />} />
          <Route path="designation" element={isSuperAdmin ? <Designation onProfileUpdate={onProfileUpdate} /> : <Navigate to="/settings/profile" replace />} />
          <Route path="reporting-manager" element={isSuperAdmin ? <ReportingManager /> : <Navigate to="/settings/profile" replace />} />
          <Route path="organization" element={isSuperAdmin ? <Organization/> : <Navigate to="/settings/profile" replace />} />
          
          {/* UPDATED: Route accessible to SUPER_ADMIN or REPORTING_MANAGER */}
          <Route path="permissions" element={isSuperAdmin || isReportingManager ? <PermissionsPage /> : <Navigate to="/settings/profile" replace />} />
          <Route
            path="maintenance"
            element={
              isSuperAdmin || isFleetManager ? (
                <MaintenanceSettingsPage />
              ) : (
                <Navigate to="/settings/profile" replace />
              )
            }
          />
          
          <Route path="*" element={<Navigate to="/settings/profile" replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default SettingPageMain;